import React from 'react'
import { ChevronDoubleLeftIcon } from '@heroicons/react/16/solid'
import ChevronDownIcon from '@heroicons/react/16/solid/ChevronDownIcon'
import _ from 'lodash'
import { isBefore } from 'date-fns'
import { Button, ButtonText } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import ButtonGroup from '@/components/ButtonGroup'
import useAppStore from '@/store/useAppStore'
import { BUDGETING_PERIODS } from '@/data/constants/constants'
import useGetVaults from '@/services/api/vault/useGetVaults'
import useGetPeriodByOffset from '@/services/api/cache/useGetPeriodByOffset'
import { useFeatureValue, UpgradeModal, LockBadge } from '@/components/feature-gate'
import { maxHorizonPeriods } from '@/utils/horizonLimits'

export default function TableControlRow() {
  const periodType = useAppStore((state) => state.periodType)
  const offset = useAppStore((state) => state.offset)
  const setOffset = useAppStore((state) => state.setOffset)

  const { data: vaults } = useGetVaults()
  const [offsetIncrement, setOffsetIncrement] = React.useState('1')

  const historyMonths = useFeatureValue('history.months', Infinity)
  const currentPeriodType =
    periodType === BUDGETING_PERIODS.WEEK ? 'Week' : 'Month'
  const maxHistoryPeriods = maxHorizonPeriods(currentPeriodType, historyMonths)
  const historyLimitReached = offset >= maxHistoryPeriods

  const [historyUpgradeOpen, setHistoryUpgradeOpen] = React.useState(false)

  const { data: oldestPeriod } = useGetPeriodByOffset({
    offset,
    position: 0,
  })

  const scrollToCurrentPeriod = () => {
    const currentPeriod = document.querySelector('table th .current-period')
    if (currentPeriod) {
      currentPeriod.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      })
    }
  }

  const vaultsFlat = _.flatten(
    vaults?.map((vault) => {
      return vault.is_group ? vault.vaults : [vault]
    }),
  )

  const oldestDateOfVault = _.minBy(
    vaultsFlat,
    (vault) => new Date(vault?.opening_balance?.transaction_date ?? Date.now()),
  )?.opening_balance?.transaction_date

  const disableBackButton =
    oldestDateOfVault &&
    oldestPeriod?.start &&
    isBefore(new Date(oldestPeriod.start), new Date(oldestDateOfVault))

  return (
    <div className="flex items-center gap-2">
      <ButtonGroup>
        <Button
          variant="outline"
          onClick={() => {
            if (historyLimitReached) {
              setHistoryUpgradeOpen(true)
              return
            }
            setOffset(parseInt(offset) + parseInt(offsetIncrement, 10))
          }}
          disabled={disableBackButton}
          className={historyLimitReached ? 'opacity-50' : ''}
        >
          <ChevronDoubleLeftIcon />
          <ButtonText>
            Show Previous: {offsetIncrement} {periodType}
          </ButtonText>
          {historyLimitReached && !disableBackButton && <LockBadge />}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger disabled={disableBackButton} asChild>
            <Button iconOnly type="button" variant="outline">
              <ChevronDownIcon />
            </Button>
          </DropdownMenuTrigger>

          {periodType === BUDGETING_PERIODS.WEEK ? (
            <WeekOffsetMenu
              offset={offsetIncrement}
              setOffset={(value) => {
                setOffsetIncrement(value)
                setOffset(parseInt(value) + parseInt(offset))
              }}
            />
          ) : (
            <MonthOffsetMenu
              offset={offsetIncrement}
              setOffset={(value) => {
                setOffsetIncrement(value)
                setOffset(parseInt(value) + parseInt(offset))
              }}
            />
          )}
        </DropdownMenu>
      </ButtonGroup>
      <Button variant="outline" onClick={scrollToCurrentPeriod}>
        Today
      </Button>

      <UpgradeModal
        feature={{ lookupKey: 'history.months', name: 'Longer history' }}
        open={historyUpgradeOpen}
        onOpenChange={setHistoryUpgradeOpen}
      />
    </div>
  )
}

export function WeekOffsetMenu({ offset, setOffset }) {
  return (
    <DropdownMenuContent className="w-[180px]">
      <DropdownMenuRadioGroup value={offset} onValueChange={setOffset}>
        <DropdownMenuRadioItem value={'1'}>1 Week</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'2'}>2 Weeks</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'4'}>4 Weeks</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'13'}>13 Weeks</DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  )
}

export function MonthOffsetMenu({ offset, setOffset }) {
  return (
    <DropdownMenuContent className="w-[180px]">
      <DropdownMenuRadioGroup value={offset} onValueChange={setOffset}>
        <DropdownMenuRadioItem value={'1'}>1 Month</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'2'}>2 Months</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'3'}>3 Months</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={'6'}>6 Months</DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  )
}
